import React, { useEffect, useMemo, useState } from "react";
import { Empty, Tooltip } from "antd";
import {
  DeleteOutlined,
  FileTextOutlined,
  LinkOutlined,
} from "@ant-design/icons";
import styled from "styled-components";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

import { FlagStatus, MilestoneKey } from "../../../enums/Status";
import {
  getEscalationLevel,
  formatEscalationCode,
} from "../../../enums/Escalations";
import {
  milestoneTagsColors,
  greyText,
  lightGreyText,
  lightGreyOutline,
  lightGreys,
  darkGreen,
  compassBordeaux,
  compassLightGreen,
  compassYellow,
  ESCALATION_COLORS,
} from "../../../styles";
import { MESSAGES } from "../../../messages";
import { formatReadable, getSdgIcon } from "../../../utils/formattingUtils";
import { StyledPlaceholder } from "../../../components/visuals/styledComponents";
import { CompassParagraph } from "../../../components/common/styledComponents";
import { FlexibleContainer } from "../../../components/containers/styledComponents";
import { Avatar, AvatarStack } from "../../../components/visuals/Avatar";
import { Momentum, MomentumDisplay } from "../../../models/stewardshipModels";
import { usePost } from "../../../hooks/usePost";
import { useToast } from "../../../store/ToastProvider";
import { ROUTES } from "../../../routes/routeNames";
import { WsQParamType, TaskStatus } from "../../../models/workflowModels";
import {
  TimelineActivityResponse,
  EngagementTimelineRequest,
} from "../../../models/stewardshipModels";
import { ScoreRing } from "../../SingleEngagement/AssessmentView/v2/ScoreRing";

import penIcon from "../../../assets/svg/interaction_grey.svg";
import workspaceIcon from "../../../assets/svg/task_grey.svg";
import votingIcon from "../../../assets/svg/vote_grey.svg";
import flagWhite from "../../../assets/svg/flag_white.svg";
import { FLAG_ICONS } from "../../../components/visuals/TaskKindIcon";

import {
  getInteractionClassImage,
  getLinkageTypeImage,
  getSequenceFlowImage,
  getInteractionClassTooltip,
  getLinkageTypeTooltip,
  getSequenceFlowTooltip,
  INTERACTION_CLASS_CONFIG,
  LINKAGE_TYPE_CONFIG,
  SEQUENCE_FLOW_CONFIG,
  ActivityType,
  TimelineActivity,
  transformTimelineResponse,
  VoteActivity,
  InteractionActivity,
  TaskActivity,
} from "../utils";
import { ActivityDetailModal } from "./ActivityDetailModal";

dayjs.extend(relativeTime);

const RAIL_X = 19;
const NODE = 22;
const NODE_Y = 7 + NODE / 2;

const Head = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
`;

const HeadMeta = styled.span`
  font-family: "Apercu";
  font-size: 12px;
  color: ${lightGreyText};
`;

const Feed = styled.div`
  max-height: 520px;
  overflow-y: auto;
  margin-top: 10px;
  padding-right: 4px;
`;

const MonthLabel = styled.div`
  position: sticky;
  top: 0;
  z-index: 2;
  background: white;
  padding: 10px 8px 4px ${RAIL_X + NODE / 2 + 13}px;
  font-family: "Apercu";
  font-size: 11.5px;
  color: ${lightGreyText};

  &:first-child {
    padding-top: 0;
  }

  &:not(:first-child)::before {
    content: "";
    position: absolute;
    left: ${RAIL_X}px;
    top: 0;
    bottom: 0;
    width: 1px;
    background: ${lightGreyOutline};
  }
`;

const Row = styled.div<{
  $first: boolean;
  $last: boolean;
  $clickable: boolean;
}>`
  position: relative;
  display: grid;
  grid-template-columns: ${NODE}px minmax(0, 1fr);
  column-gap: 12px;
  padding: 7px 8px;
  border-radius: 8px;
  cursor: ${({ $clickable }): string => ($clickable ? "pointer" : "default")};
  transition: background 0.15s ease;

  &::before {
    content: "";
    position: absolute;
    left: ${RAIL_X}px;
    width: 1px;
    top: ${({ $first }): string => ($first ? `${NODE_Y}px` : "0")};
    bottom: ${({ $last }): string =>
      $last ? `calc(100% - ${NODE_Y}px)` : "0"};
    background: ${lightGreyOutline};
  }

  &:hover {
    background: ${lightGreys.KPIBackground};
  }

  &:hover .tl-delete {
    opacity: 1;
  }
`;

const Node = styled.span<{ $tone: string }>`
  position: relative;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${NODE}px;
  height: ${NODE}px;
  border-radius: 50%;
  background: white;
  box-shadow: inset 0 0 0 1.5px ${({ $tone }): string => $tone};

  img {
    width: 11px;
    height: 11px;
  }
`;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const Line = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: ${NODE}px;
  min-width: 0;
`;

const Title = styled.span<{ $link?: boolean }>`
  font-family: "ApercuMedium";
  font-size: 13px;
  color: ${darkGreen};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;

  &:hover {
    text-decoration: ${({ $link }): string => ($link ? "underline" : "none")};
  }
`;

const Snippet = styled.span`
  font-family: "Apercu";
  font-size: 12.5px;
  line-height: 1.5;
  color: ${greyText};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
`;

const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 18px;
  padding: 0 6px;
  border-radius: 4px;
  flex-shrink: 0;
  font-family: "Apercu";
  font-size: 11px;
  color: ${greyText};
  white-space: nowrap;
  box-shadow: inset 0 0 0 1px ${lightGreyOutline};
`;

const Dot = styled.span<{ $tint: string }>`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${({ $tint }): string => $tint};
`;

const Meta = styled.span`
  font-family: "Apercu";
  font-size: 12px;
  color: ${lightGreyText};
  white-space: nowrap;
  flex-shrink: 0;
`;

const Spacer = styled.span`
  flex: 1;
  min-width: 8px;
`;

const Cluster = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

const DateText = styled.span`
  font-family: "ApercuMono", "Apercu", monospace;
  font-size: 11px;
  color: ${lightGreyText};
  min-width: 44px;
  text-align: right;
  flex-shrink: 0;
`;

const DeleteBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  padding: 0;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: ${lightGreyText};
  font-size: 12px;
  cursor: pointer;
  opacity: 0;
  transition:
    opacity 0.15s ease,
    color 0.15s ease;

  &:hover,
  &:focus-visible {
    opacity: 1;
    color: ${compassBordeaux};
  }
`;

const FlagImg = styled.img`
  width: 14px;
  height: 14px;
`;

const MOMENTUM_TINT: Record<Momentum, string> = {
  [Momentum.PROGRESSING]: compassLightGreen,
  [Momentum.STALLING]: compassYellow,
  [Momentum.UNKNOWN]: lightGreyText,
};

const TYPE_ICON: Record<ActivityType, string> = {
  [ActivityType.VOTE]: votingIcon,
  [ActivityType.INTERACTION]: penIcon,
  [ActivityType.TASK]: workspaceIcon,
};

const TYPE_NOUN: Record<ActivityType, string> = {
  [ActivityType.VOTE]: "vote",
  [ActivityType.INTERACTION]: "interaction",
  [ActivityType.TASK]: "task",
};

const FLAG_LABELS: Record<TaskStatus, string> = {
  [TaskStatus.NOT_STARTED]: "Flagged",
  [TaskStatus.IN_PROGRESS]: "Flagged",
  [TaskStatus.STUCK]: FlagStatus.BLOCKED,
  [TaskStatus.COMPLETED]: FlagStatus.RESOLVED,
};

const flagFor = (status?: TaskStatus): { icon: string; label: string } =>
  status
    ? { icon: FLAG_ICONS[status], label: FLAG_LABELS[status] }
    : { icon: flagWhite, label: "Flag" };

const confidenceTint = (pct: number): string => {
  if (pct >= 75) return compassLightGreen;
  if (pct >= 50) return compassYellow;
  return lightGreyText;
};

const parseSdgNumber = (sdg: string): number => {
  const match = sdg.match(/\d+/);
  return match ? parseInt(match[0], 10) : 0;
};

const SDG_NAMES: Record<number, string> =
  MESSAGES.STEWARDSHIP.ENGAGEMENT_MODULE.SDG_NAMES;

const getSdgTooltip = (sdg: string): string => {
  const name = SDG_NAMES[parseSdgNumber(sdg)];
  return name ? `${sdg}: ${name}` : sdg;
};

const escalationMessages =
  MESSAGES.STEWARDSHIP.ENGAGEMENT_MODULE.ESCALATION_MESSAGES;

const ESCALATION_DESCRIPTIONS: Record<string, string> = {
  ...escalationMessages.E1,
  ...escalationMessages.E2,
  ...escalationMessages.E3,
  ...escalationMessages.E4,
  ...escalationMessages.E5,
};

const getEscalationTooltip = (key: string): string => {
  const description = ESCALATION_DESCRIPTIONS[key];
  return description
    ? `${formatEscalationCode(key)}: ${description}`
    : formatEscalationCode(key);
};

const shortDate = (date: string): string => {
  const d = dayjs(date);
  if (!d.isValid()) return "—";
  return d.year() === dayjs().year() ? d.format("D MMM") : d.format("D MMM YY");
};

const fullDate = (date: string): string => {
  const d = dayjs(date);
  return d.isValid() ? `${d.format("D MMM YYYY")} · ${d.fromNow()}` : "";
};

const monthKey = (date: string): string => {
  const d = dayjs(date);
  return d.isValid() ? d.format("MMMM YYYY") : "Undated";
};

const openTask = (taskId: string): void => {
  const params = new URLSearchParams({ taskId }).toString();
  window.open(
    `${ROUTES.WORKSPACE}/${WsQParamType.VIEW_TASK}?${params}`,
    "_blank",
    "noopener,noreferrer",
  );
};

const stopPropagation = (e: React.MouseEvent): void => e.stopPropagation();

type VoteLineProps = {
  activity: VoteActivity;
  managerName?: string | null;
  managerImage?: string | null;
};

const VoteLine = ({
  activity,
  managerName,
  managerImage,
}: VoteLineProps): JSX.Element => {
  const vm = activity.voting_mapping;
  const pct = Math.round(vm.confidence_score * 100);

  return (
    <>
      <Title>Voting record</Title>
      {vm.is_cross_manager && (
        <Tooltip title="Vote cast by a different manager than the engagement owner">
          <Chip>Cross-manager</Chip>
        </Tooltip>
      )}
      <Spacer />
      <Cluster onClick={stopPropagation}>
        <AvatarStack
          enableExpand={false}
          items={[
            {
              username:
                INTERACTION_CLASS_CONFIG[vm.interaction_class]?.displayName ||
                "",
              displayText: getInteractionClassTooltip(vm.interaction_class),
              image: getInteractionClassImage(vm.interaction_class),
              size: "xsmall",
              tooltip: true,
            },
            {
              username: LINKAGE_TYPE_CONFIG[vm.linkage_type]?.displayName || "",
              displayText: getLinkageTypeTooltip(vm.linkage_type),
              image: getLinkageTypeImage(vm.linkage_type),
              size: "xsmall",
              tooltip: true,
            },
            {
              username:
                SEQUENCE_FLOW_CONFIG[vm.sequence_flow]?.displayName || "",
              displayText: getSequenceFlowTooltip(vm.sequence_flow),
              image: getSequenceFlowImage(vm.sequence_flow),
              size: "xsmall",
              tooltip: true,
            },
          ]}
        />
        {managerName && (
          <Avatar
            username={managerName}
            image={managerImage || undefined}
            size="xsmall"
            tooltip
          />
        )}
        <ScoreRing
          value={pct}
          tint={confidenceTint(pct)}
          glyph={<LinkOutlined />}
          size="sm"
          tooltip={`Match confidence · ${pct}%`}
        />
      </Cluster>
    </>
  );
};

const InteractionLine = ({
  activity,
}: {
  activity: InteractionActivity;
}): JSX.Element => {
  const sdgs = activity.sdgs ?? [];
  const escalations = activity.escalations ?? [];

  return (
    <>
      <Title>
        {activity.interactionTitle || formatReadable(activity.type)}
      </Title>
      {activity.momentum && (
        <Tooltip title="Momentum">
          <Chip>
            <Dot $tint={MOMENTUM_TINT[activity.momentum]} />
            {MomentumDisplay[activity.momentum]}
          </Chip>
        </Tooltip>
      )}
      <Spacer />
      <Cluster onClick={stopPropagation}>
        {sdgs.length > 0 && (
          <AvatarStack
            maxCount={3}
            items={sdgs.map((sdg) => ({
              username: sdg,
              displayText: getSdgTooltip(sdg),
              image: getSdgIcon(parseSdgNumber(sdg)),
              size: "xsmall",
              tooltip: true,
            }))}
          />
        )}
        {escalations.length > 0 && (
          <AvatarStack
            maxCount={3}
            items={escalations.map((esc) => ({
              username: formatEscalationCode(esc),
              displayText: getEscalationTooltip(esc),
              size: "xsmall",
              tooltip: true,
              bgColor: ESCALATION_COLORS[getEscalationLevel(esc)],
            }))}
          />
        )}
      </Cluster>
    </>
  );
};

const TaskLine = ({ activity }: { activity: TaskActivity }): JSX.Element => {
  const notes = activity.notes ?? [];
  const meta = [
    activity.priority,
    activity.deadline ? `Due ${shortDate(activity.deadline)}` : undefined,
  ].filter((m): m is NonNullable<typeof m> => Boolean(m));

  return (
    <>
      <Tooltip title="Open in workspace">
        <Title $link>
          {activity.description || formatReadable(activity.type)}
        </Title>
      </Tooltip>
      {meta.length > 0 && <Meta>{meta.join(" · ")}</Meta>}
      <Spacer />
      <Cluster onClick={stopPropagation}>
        {notes.length > 0 && (
          <Tooltip
            title={notes.map((note, i) => (
              <div key={i}>• {note.title || note.content}</div>
            ))}
          >
            <Meta>
              <FileTextOutlined /> {notes.length}
            </Meta>
          </Tooltip>
        )}
        {activity.is_flag && (
          <Tooltip title={flagFor(activity.status).label}>
            <FlagImg
              src={flagFor(activity.status).icon}
              alt={flagFor(activity.status).label}
            />
          </Tooltip>
        )}
        {activity.assignee && (
          <Avatar username={activity.assignee} size="xsmall" tooltip />
        )}
      </Cluster>
    </>
  );
};

const detailText = (activity: TimelineActivity): string | undefined => {
  switch (activity.type) {
    case ActivityType.VOTE:
      return activity.voting_mapping.rationale;
    case ActivityType.INTERACTION:
      return activity.details;
    case ActivityType.TASK:
      return activity.details;
  }
};

interface EngagementTimelineProps {
  engagementRecordId: string | null;
  engagementCategory?: string;
  refreshKey?: number;
  onDeleteUpdate?: (updateId: string) => void;
  isExternalLoading?: boolean;
  /** Engagement-owning manager — shown on VOTE rows when voting manager is not on the payload. */
  managerName?: string | null;
  managerImage?: string | null;
}

type MonthGroup = { month: string; items: TimelineActivity[] };

export const EngagementTimeline = ({
  engagementRecordId,
  engagementCategory,
  refreshKey,
  onDeleteUpdate,
  isExternalLoading,
  managerName,
  managerImage,
}: EngagementTimelineProps): JSX.Element => {
  const isCorporateIssue = engagementCategory === "Corporate Issue";
  const [timelineData, setTimelineData] = useState<TimelineActivity[]>([]);
  const [selectedActivity, setSelectedActivity] = useState<
    VoteActivity | InteractionActivity | null
  >(null);
  const { addToast } = useToast();

  const { data, sendData, error, isLoading } = usePost<
    TimelineActivityResponse[],
    EngagementTimelineRequest
  >();

  useEffect(() => {
    if (engagementRecordId) {
      sendData("GET_ENGAGEMENT_TIMELINE", {
        engagement_record_id: engagementRecordId,
      });
    }
  }, [engagementRecordId, refreshKey]);

  useEffect(() => {
    if (data) {
      setTimelineData(transformTimelineResponse(data));
    }
  }, [data]);

  useEffect(() => {
    if (error) {
      addToast("An error occurred while fetching engagement timeline", "error");
    }
  }, [error]);

  const sorted = useMemo(
    () =>
      timelineData
        .slice()
        .sort(
          (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
        ),
    [timelineData],
  );

  const groups = useMemo(
    () =>
      sorted.reduce<MonthGroup[]>((acc, activity) => {
        const month = monthKey(activity.date);
        const last = acc[acc.length - 1];
        if (last && last.month === month) {
          last.items.push(activity);
        } else {
          acc.push({ month, items: [activity] });
        }
        return acc;
      }, []),
    [sorted],
  );

  const summary = useMemo(() => {
    const counts = sorted.reduce<Partial<Record<ActivityType, number>>>(
      (acc, a) => ({ ...acc, [a.type]: (acc[a.type] ?? 0) + 1 }),
      {},
    );
    return Object.values(ActivityType)
      .filter((t) => counts[t])
      .map((t) => {
        const n = counts[t] ?? 0;
        return `${n} ${TYPE_NOUN[t]}${n > 1 ? "s" : ""}`;
      })
      .join(" · ");
  }, [sorted]);

  const toneFor = (activity: TimelineActivity): string => {
    if (isCorporateIssue) return milestoneTagsColors[MilestoneKey.CI]?.bg;
    if (activity.milestone) return milestoneTagsColors[activity.milestone]?.bg;
    return lightGreyOutline;
  };

  const handleRowClick = (activity: TimelineActivity): void => {
    if (activity.type === ActivityType.TASK) {
      openTask(activity.id);
      return;
    }
    setSelectedActivity(activity);
  };

  const isBusy = isLoading || isExternalLoading;
  const total = sorted.length;
  let index = 0;

  return (
    <div>
      <Head>
        <CompassParagraph fontSize={16} bold>
          Engagement Timeline
        </CompassParagraph>
        {!isBusy && total > 0 && <HeadMeta>{summary}</HeadMeta>}
      </Head>

      {isBusy ? (
        <FlexibleContainer fg={1} as="stretch" fd="column" mt="10px">
          <StyledPlaceholder xs={10} size="lg" bg="white" />
          <StyledPlaceholder xs={10} size="lg" bg="white" />
          <StyledPlaceholder xs={10} size="lg" bg="white" />
        </FlexibleContainer>
      ) : total === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="No timeline activities"
        />
      ) : (
        <Feed>
          {groups.map((group) => (
            <React.Fragment key={group.month}>
              <MonthLabel>{group.month}</MonthLabel>
              {group.items.map((activity) => {
                const i = index++;
                const text = detailText(activity);
                const milestoneLabel = isCorporateIssue
                  ? "Corporate issue"
                  : activity.milestone
                    ? formatReadable(activity.milestone)
                    : undefined;

                return (
                  <Row
                    key={activity.id}
                    $first={i === 0}
                    $last={i === total - 1}
                    $clickable
                    onClick={(): void => handleRowClick(activity)}
                  >
                    <Tooltip title={milestoneLabel} placement="left">
                      <Node $tone={toneFor(activity)}>
                        <img src={TYPE_ICON[activity.type]} alt="" />
                      </Node>
                    </Tooltip>
                    <Body>
                      <Line>
                        {activity.type === ActivityType.VOTE && (
                          <VoteLine
                            activity={activity}
                            managerName={managerName}
                            managerImage={managerImage}
                          />
                        )}
                        {activity.type === ActivityType.INTERACTION && (
                          <InteractionLine activity={activity} />
                        )}
                        {activity.type === ActivityType.TASK && (
                          <TaskLine activity={activity} />
                        )}
                        <Tooltip title={fullDate(activity.date)}>
                          <DateText>{shortDate(activity.date)}</DateText>
                        </Tooltip>
                        {activity.type === ActivityType.INTERACTION &&
                          onDeleteUpdate && (
                            <Tooltip title="Delete update">
                              <DeleteBtn
                                className="tl-delete"
                                type="button"
                                aria-label="Delete update"
                                onClick={(e): void => {
                                  e.stopPropagation();
                                  onDeleteUpdate(activity.id);
                                }}
                              >
                                <DeleteOutlined />
                              </DeleteBtn>
                            </Tooltip>
                          )}
                      </Line>
                      {text && <Snippet>{text}</Snippet>}
                    </Body>
                  </Row>
                );
              })}
            </React.Fragment>
          ))}
        </Feed>
      )}

      <ActivityDetailModal
        show={selectedActivity !== null}
        onClose={(): void => setSelectedActivity(null)}
        activity={selectedActivity}
        managerName={managerName}
        managerImage={managerImage}
      />
    </div>
  );
};
